import React, { useEffect, useRef, useState } from "react";
import { Error as ErrorIcon } from "@mui/icons-material";
import * as pdfjsLib from "pdfjs-dist/webpack.mjs";

type PdfDocument = {
  numPages: number;
  getPage: (pageNum: number) => Promise<{
    getViewport: (options: { scale: number }) => { width: number; height: number };
    render: (options: {
      canvasContext: CanvasRenderingContext2D;
      viewport: { width: number; height: number };
      canvas: HTMLCanvasElement;
    }) => { promise: Promise<void> };
  }>;
  destroy: () => void;
};

const detectPreviewFileType = (url: string): "pdf" | "image" | "html" => {
  const path = url.split("?")[0].toLowerCase();
  if (path.endsWith(".pdf")) return "pdf";
  if (/\.(jpe?g|png|gif|webp|svg)$/.test(path)) return "image";
  if (path.endsWith(".html") || path.endsWith(".htm")) return "html";
  return "pdf";
};

export type SubmittedAgreementPreviewProps = {
  fileUrl: string | null | undefined;
  variant?: "desktop" | "mobile";
};

const SubmittedAgreementPreview: React.FC<SubmittedAgreementPreviewProps> = ({
  fileUrl,
  variant = "desktop",
}) => {
  const [fileType, setFileType] = useState<string | null>(null);
  const [pdfNumPages, setPdfNumPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const pdfDocRef = useRef<PdfDocument | null>(null);
  const pdfCanvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);
  const isMobile = variant === "mobile";

  useEffect(() => {
    let cancelled = false;

    const loadPreview = async () => {
      setIsLoading(true);
      setError(null);
      setPdfNumPages(0);
      if (pdfDocRef.current) {
        pdfDocRef.current.destroy();
        pdfDocRef.current = null;
      }

      if (!fileUrl?.trim()) {
        setError("No agreement file available.");
        setIsLoading(false);
        return;
      }
      if (!/^https?:\/\//i.test(fileUrl.trim())) {
        setError("Document URL is invalid.");
        setIsLoading(false);
        return;
      }

      try {
        const resolvedType = detectPreviewFileType(fileUrl);
        setFileType(resolvedType);

        if (resolvedType === "pdf") {
          const response = await fetch(fileUrl);
          if (!response.ok) {
            throw new Error(`Failed to fetch PDF (${response.status})`);
          }
          const arrayBuffer = await response.arrayBuffer();
          if (cancelled) return;

          const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
          if (cancelled) {
            pdf.destroy();
            return;
          }

          pdfDocRef.current = pdf;
          setPdfNumPages(pdf.numPages);
        }
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : "Failed to load agreement.";
        setError(message);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadPreview();

    return () => {
      cancelled = true;
      if (pdfDocRef.current) {
        pdfDocRef.current.destroy();
        pdfDocRef.current = null;
      }
    };
  }, [fileUrl]);

  useEffect(() => {
    const pdf = pdfDocRef.current;
    if (!pdf || pdfNumPages === 0) return;

    let cancelled = false;

    const renderPages = async () => {
      for (let pageNum = 1; pageNum <= pdfNumPages; pageNum++) {
        if (cancelled) return;
        const canvas = pdfCanvasRefs.current[pageNum - 1];
        if (!canvas) continue;

        try {
          const page = await pdf.getPage(pageNum);
          const viewport = page.getViewport({ scale: 1 });
          const context = canvas.getContext("2d");
          if (!context) continue;

          canvas.height = viewport.height;
          canvas.width = viewport.width;
          await page.render({ canvasContext: context, viewport, canvas }).promise;
        } catch (renderErr) {
          if (cancelled) return;
          const message =
            renderErr instanceof Error ? renderErr.message : "Failed to render PDF page";
          setError(message);
        }
      }
    };

    renderPages();

    return () => {
      cancelled = true;
    };
  }, [pdfNumPages]);

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center p-6 w-full"
        data-testid="submitted-agreement-preview-loading"
      >
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primaryDark-200" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="flex flex-col items-center justify-center p-4 text-center w-full"
        data-testid="submitted-agreement-preview-error"
      >
        <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-2">
          <ErrorIcon className="text-red-500" sx={{ fontSize: 24 }} />
        </div>
        <p className="text-[13px] font-open-sans text-red-600">{error}</p>
      </div>
    );
  }

  if (fileType === "image" && fileUrl) {
    return (
      <div
        className={`w-full overflow-y-auto rounded-lg border border-[#E9E9E9] bg-white mb-4 ${
          isMobile ? "max-h-[240px] min-h-[120px] p-2" : "max-h-[280px] min-h-[140px] p-3"
        }`}
        data-testid="submitted-agreement-preview-image"
      >
        <img
          src={fileUrl}
          alt="Signed membership agreement"
          className="max-w-full object-contain mx-auto"
        />
      </div>
    );
  }

  if (fileType === "html" && fileUrl) {
    return (
      <div
        className={`w-full overflow-y-auto rounded-lg border border-[#E9E9E9] bg-white mb-4 ${
          isMobile ? "max-h-[240px] min-h-[120px]" : "max-h-[280px] min-h-[140px]"
        }`}
        data-testid="submitted-agreement-preview-html"
      >
        <iframe
          src={fileUrl}
          title="Signed membership agreement"
          className="w-full h-full min-h-[200px] border-0"
        />
      </div>
    );
  }

  if (fileType === "pdf" && pdfNumPages > 0) {
    return (
      <div
        className={`w-full overflow-y-auto rounded-lg border border-[#E9E9E9] bg-white mb-4 ${
          isMobile ? "max-h-[240px] min-h-[120px] p-2" : "max-h-[280px] min-h-[140px] p-3"
        }`}
        data-testid="submitted-agreement-preview-pdf"
      >
        {Array.from({ length: pdfNumPages }, (_, index) => (
          <canvas
            key={index}
            ref={(el) => {
              pdfCanvasRefs.current[index] = el;
            }}
            data-testid={index === 0 ? "submitted-agreement-pdf-canvas" : undefined}
            aria-label={`Signed agreement page ${index + 1}`}
            className="mx-auto mb-3 max-w-full shadow-sm"
          />
        ))}
      </div>
    );
  }

  return null;
};

export default SubmittedAgreementPreview;
